/**
 * Целият API в един файл — огледало на `app/backend/config/api.py`.
 *
 * Пътищата се пишат тук, не в екраните: когато рутерът на Django се промени,
 * се пипа само този файл.
 */

const detail = (base: string) => (id: number | string) => `${base}${id}/`

export const endpoints = {
  employeeWorkSchedule: {
    list: 'work-schedule/', fill: 'work-schedule/fill/', day: 'work-schedule/day/', days: 'work-schedule/days/', export: 'work-schedule/export/',
  },
  intakeOffice: {
    link: detail('intake-links/'),
    blocks: 'intake-blocks/',
    block: detail('intake-blocks/'),
    review: (id: number, action: 'approve' | 'reject' | 'pause' | 'resume') => `intake-blocks/${id}/${action}/`,
  },
  intake: {
    context: 'intake/',
    blocks: 'intake/blocks/',
    block: (id: number) => `intake/blocks/${id}/`,
    submit: 'intake/submit/',
    reopen: (id: number) => `intake/blocks/${id}/reopen/`,
    advances: 'intake/advances/',
  },
  /** Кой и кога е създал и последно променил запис — `attribution/contracts.Contract/3/`. */
  attribution: (model: string, id: number | string) => `attribution/${model}/${id}/`,
  /** Отчетите от обектите — по телефона или ръчно. PATCH пази черновата. */
  siteReports: {
    list: 'site-reports/',
    detail: detail('site-reports/'),
    approve: (id: number | string) => `site-reports/${id}/approve/`,
    reject: (id: number | string) => `site-reports/${id}/reject/`,
    reopen: (id: number | string) => `site-reports/${id}/reopen/`,
  },
  /** Номерата, от които се обаждат хората на контрагента. */
  reportPhones: {
    list: 'report-phones/',
    detail: detail('report-phones/'),
  },
  scans: {
    list: 'document-scans/',
    detail: detail('document-scans/'),
    approve: (id: number | string) => `document-scans/${id}/approve/`,
    attach: (id: number | string) => `document-scans/${id}/attach/`,
    reject: (id: number | string) => `document-scans/${id}/reject/`,
    retry: (id: number | string) => `document-scans/${id}/retry/`,
    draft: (id: number | string) => `document-scans/${id}/draft/`,
    duplicateCheck: (id: number | string) => `document-scans/${id}/duplicate-check/`,
  },
  auth: {
    login: 'auth/login/',
    logout: 'auth/logout/',
    me: 'auth/me/',
  },

  /** Настройки → Потребители — само администраторите. */
  users: {
    list: 'users/',
    detail: detail('users/'),
  },
  /** Нарядите по обектите на техника — само за четене. */
  siteNaryads: {
    list: 'site-naryads/',
    detail: detail('site-naryads/'),
  },
  companies: {
    list: 'companies/',
    detail: detail('companies/'),
  },
  paymentAccounts: {
    list: 'payment-accounts/',
    detail: detail('payment-accounts/'),
  },

  counterparties: {
    list: 'counterparties/',
    detail: detail('counterparties/'),
    /** Сметката на един контрагент: суми, аванси, месеци, обекти. */
    summary: (id: number | string) => `counterparties/${id}/summary/`,
  },
  /** Визитки — хора за връзка (адвокат, нотариус…) и обектите им. */
  businessCards: {
    list: 'business-cards/',
    detail: detail('business-cards/'),
    roles: 'business-cards/roles/',
  },
  businessCardProjects: {
    list: 'business-card-projects/',
    detail: detail('business-card-projects/'),
  },
  /** Старите бригади на контрагенти — сървърът вече не ги обслужва. */
  crews: {
    list: 'crews/',
    detail: detail('crews/'),
  },
  skills: {
    list: 'skills/',
    detail: detail('skills/'),
  },
  costCentres: {
    list: 'cost-centres/',
    detail: detail('cost-centres/'),
  },
  categories: {
    list: 'categories/',
    detail: detail('categories/'),
  },
  units: {
    list: 'units/',
    detail: detail('units/'),
  },

  projects: {
    list: 'projects/',
    detail: detail('projects/'),
    /** Всичко за един обект: дружество, суми, месеци, контрагенти. */
    summary: (id: number | string) => `projects/${id}/summary/`,
    /** Редовете от фактурите и нарядите — `?kategoria=<id|bez|naryadi>&grupa=&company=&search=`. */
    /** PUT {amounts: {код: сума|null}, reason} — бюджетът по групи наведнъж. */
    budget: (id: number | string) => `projects/${id}/budget/`,
    expenses: (id: number | string) => `projects/${id}/expenses/`,
    /** Картите по категория; без избраната, за да остават всички. */
    expenseCategories: (id: number | string) => `projects/${id}/expenses/categories/`,
    expenseTotals: (id: number | string) => `projects/${id}/expenses/totals/`,
    /** Всички разходи и тези за текущата година: фактури с ДДС + касата. */
    expenseOverview: (id: number | string) => `projects/${id}/expenses/overview/`,
    construction: (id: number | string) => `projects/${id}/construction/`,
    planningOptions: 'projects/planning-options/',
    planPreview: 'projects/plan-preview/',
    wizard: 'projects/wizard/',
    portfolio: 'projects/portfolio/',
    contractors: (id: number | string) => `projects/${id}/contractors/`,
    naryadContractors: (id: number | string) => `projects/${id}/naryad-contractors/`,
    /** Датите на актовете по сгради; PUT записва една сграда. */
    actDates: (id: number | string) => `projects/${id}/act-dates/`,
    signals: (id: number | string) => `projects/${id}/signals/`,
    plan: (id: number | string) => `projects/${id}/plan/`,
    replan: (id: number | string) => `projects/${id}/replan/`,
    startPlanning: (id: number | string) => `projects/${id}/start-planning/`,
    approvePlan: (id: number | string) => `projects/${id}/approve-plan/`,
    lifecycle: (id: number | string) => `projects/${id}/lifecycle/`,
  },

  buildings: {
    list: 'buildings/',
    detail: detail('buildings/'),
  },

  stages: {
    list: 'stages/',
    detail: detail('stages/'),
    workspace: (id: number | string) => `stages/${id}/workspace/`,
    history: (id: number | string) => `stages/${id}/history/`,
    paper: (id: number | string) => `stages/${id}/checklist-paper/`,
    confirm: (id: number | string) => `stages/${id}/confirm/`,
    checklist: (id: number | string) => `stages/${id}/checklist/`,
  },

  stageComments: {
    list: 'stage-comments/',
    resolve: (id: number | string) => `stage-comments/${id}/resolve/`,
  },

  checklistItems: {
    detail: detail('checklist-items/'),
  },

  budgetLines: {
    list: 'budget-lines/',
    forecast: (id: number | string) => `budget-lines/${id}/forecast/`,
  },

  otherExpenses: {
    list: 'other-expenses/',
    detail: detail('other-expenses/'),
  },

  landDeals: {
    detail: detail('land-deals/'),
  },

  landEntitlements: {
    list: 'land-entitlements/',
    detail: detail('land-entitlements/'),
  },

  dealTimeEntries: {
    list: 'deal-time-entries/',
    detail: detail('deal-time-entries/'),
  },

  workOrders: {
    list: 'work-orders/',
    detail: detail('work-orders/'),
    approve: (id: number | string) => `work-orders/${id}/approve/`,
    recalculate: (id: number | string) => `work-orders/${id}/recalculate/`,
    /** ?counterparty=<id>[&company=<id>] → наличен аванс за приспадане. */
    advanceBalance: 'work-orders/advance_balance/',
    /** POST {advance, amount} — приспада аванс и намалява остатъка му. */
    recoverAdvance: (id: number | string) => `work-orders/${id}/recover_advance/`,
  },

  /** Новият наряд: наряд → контрагент → редове и плащания. */
  naryads: {
    list: 'naryads/',
    detail: detail('naryads/'),
    close: (id: number | string) => `naryads/${id}/close/`,
    entries: (id: number | string) => `naryads/${id}/entries/`,
    payees: (id: number | string) => `naryads/${id}/payees/`,
    /** POST {counterparty, lines[]} — контрагент и редовете му влизат в наряда. */
    add: (id: number | string) => `naryads/${id}/add/`,
    /** POST {counterparty, lines[]} — нова чернова; редовете не се проверяват. */
    draft: (id: number | string) => `naryads/${id}/draft/`,
    /** ?counterparty=<id>[&company=<id>] → авансите с остатък за приспадане. */
    openAdvances: 'naryads/open_advances/',
    advances: 'naryads/advances/',
    /** ?counterparty=<id>[&company=<id>] → договори с остатък, КСС с остатък, аванси. */
    openWork: 'naryads/open_work/',
    schedule: 'naryads/schedule/',
  },
  naryadEntries: {
    /** ?counterparty= | ?project= | ?naryad= | ?is_draft= — за картоните. */
    list: 'naryad-entries/',
    detail: detail('naryad-entries/'),
    pay: (id: number | string) => `naryad-entries/${id}/pay/`,
    /** PUT {counterparty, lines[]} — поправя черновата. */
    draft: (id: number | string) => `naryad-entries/${id}/draft/`,
    /** POST {counterparty, lines[]} — „Добави в наряда“. */
    finalize: (id: number | string) => `naryad-entries/${id}/finalize/`,
  },
  naryadLines: {
    detail: detail('naryad-lines/'),
  },
  naryadPayments: {
    detail: detail('naryad-payments/'),
    update: detail('naryad-payments/'),
  },
  loans: {
    list: 'loans/',
    detail: detail('loans/'),
    /** Един ред на човек: взети, върнати, остатък. `?search=` */
    balances: 'loans/balances/',
    /** `?kind=counterparty|employee&id=` или `?kind=other&name=` */
    statement: 'loans/statement/',
    /** `?company=&former=1` — само имена, без досието. */
    employees: 'loans/employees/',
    /** `?amount=&date=` — невързани разходи в касата със същата сума. */
    kasaCandidates: 'loans/kasa-candidates/',
  },

  advances: {
    list: 'advances/',
    detail: detail('advances/'),
  },

  transactions: {
    list: 'transactions/',
    detail: detail('transactions/'),
    /** Наличности по всички активни сметки. */
    balances: 'transactions/balances/',
    /** POST {work_order, amount} — погасява наряд с това движение. */
    settle: (id: number | string) => `transactions/${id}/settle/`,
    /** Движения, които още не са разпределени по наряди. */
    unsettled: 'transactions/unsettled/',
    /** ПКО/РКО за печат. */
    cashOrder: (id: number | string) => `transactions/${id}/cash-order/`,
    /** GET ?account&direction&date — серия и номер на следващия ордер. */
    nextNumber: 'transactions/next-number/',
  },

  /** Договорът е обещаното: стойност, КСС и усвояване по нея. */
  contracts: {
    list: 'contracts/',
    detail: detail('contracts/'),
    /** КСС-то с изпълненото по всеки ред. */
    items: (id: number | string) => `contracts/${id}/items/`,
    recalculate: (id: number | string) => `contracts/${id}/recalculate/`,
    start: (id: number | string) => `contracts/${id}/start/`,
    /** POST {on?, note?} — „Завършена и приета“. */
    accept: (id: number | string) => `contracts/${id}/accept/`,
    reopen: (id: number | string) => `contracts/${id}/reopen/`,
    naryadLines: (id: number | string) => `contracts/${id}/naryad_lines/`,
    /** Календарът на активните контрагенти — `?start=&end=&project=`. */
    occupancy: 'contracts/occupancy/',
    /** Кой какво има записано в период — `?start=&end=`. */
    availability: 'contracts/availability/',
  },
  contractItems: {
    list: 'contract-items/',
    detail: detail('contract-items/'),
  },

  employees: {
    list: 'employees/',
    detail: detail('employees/'),
    /** Месечният разход за персонал, общо и по отдели. */
    monthlyCost: 'employees/monthly_cost/',
    summary: 'employees/summary/',
    /** Полагаемият отпуск за година — `?year=&hired_on=&left_on=&annual_days=`. */
    leaveEntitlement: 'employees/leave_entitlement/',
  },

  /** Бригадите от наши служители. */
  staffCrews: {
    list: 'staff-crews/',
    detail: detail('staff-crews/'),
  },
  crewAssignments: {
    list: 'crew-assignments/',
    detail: detail('crew-assignments/'),
    cancel: (id: number) => `crew-assignments/${id}/cancel/`,
    /** Графикът на бригадите — `?start=&end=`. */
    schedule: 'crew-assignments/schedule/',
  },

  employeeLeaves: {
    list: 'employee-leaves/',
    preview: 'employee-leaves/preview/',
    calendar: 'employee-leaves/calendar/',
    decision: (id: number) => `employee-leaves/${id}/decision/`,
  },
  leaveAllowances: {
    list: 'leave-allowances/',
    detail: detail('leave-allowances/'),
  },
  materials: {
    list: 'materials/',
    detail: detail('materials/'),
  },
  materialRequestNotes: {
    list: 'material-request-notes/',
    detail: detail('material-request-notes/'),
  },
  deliveries: {
    list: 'deliveries/',
    detail: detail('deliveries/'),
  },

  invoices: {
    list: 'invoices/',
    detail: detail('invoices/'),
    recalculate: (id: number | string) => `invoices/${id}/recalculate/`,
    /** Оборот по вид документ. Кредитните известия се вадят, не се събират. */
    totals: 'invoices/totals/',
    /** Опашката за плащане: остатък, брой, просрочени, по начин на плащане. */
    payables: 'invoices/payables/',
    /** Плаща изцяло или на части. Без `amount` — плаща се остатъкът. */
    pay: (id: number | string) => `invoices/${id}/pay/`,
    /** Сваля последното плащане. */
    unpay: (id: number | string) => `invoices/${id}/unpay/`,
    /** Анулира — фактурата не се трие, отива в „Анулирани“. */
    cancel: (id: number | string) => `invoices/${id}/cancel/`,
    restore: (id: number | string) => `invoices/${id}/restore/`,
  },
  /**
   * ДДС чакалнята на едно дружество. Всичко иска `company`; месецът е `YYYY-MM`.
   * Действията връщат картите (`summary`) наново.
   */
  vat: {
    /** ?company=&month= — картите: чакащи, ДДС от продажби, включено, салдо. */
    summary: 'vat/summary/',
    /** ?company= — месеците със запис, най-новият първи. */
    periods: 'vat/periods/',
    /** ?company=&state=waiting|included|unknown&month=&search=&limit=&offset= */
    invoices: 'vat/invoices/',
    /** POST {company, month, invoice_ids} — всичките или нито една. */
    include: 'vat/include/',
    /** POST {company, month, invoice_ids} — „Върни в чакащи“. */
    exclude: 'vat/exclude/',
    /** PUT {company, month, sales_vat} — null изчиства. */
    salesVat: 'vat/sales-vat/',
    /** POST {company, month} — само администратор. */
    close: 'vat/close/',
    reopen: 'vat/reopen/',
    /** ?company=&month= — xlsx за счетоводителя. */
    export: 'vat/export/',
  },
  invoicePayments: {
    list: 'invoice-payments/',
    detail: detail('invoice-payments/'),
  },
  invoiceLines: {
    list: 'invoice-lines/',
    detail: detail('invoice-lines/'),
    /**
     * Редовете на един обект. Ползвай `for_project`, не `project`: вносът е
     * записал обекта на главата на фактурата и `project=<id>` връща нула.
     */
    forProject: (id: number | string) => `invoice-lines/?for_project=${id}`,
    /** Сборът по текущия филтър — не по заредената страница. */
    totals: 'invoice-lines/totals/',
    materialGroups: 'invoice-lines/material-groups/',
  },

  /**
   * Документи. Списъкът приема `counterparty`, `project` или `work_order` —
   * панелът пита само за своя адресат.
   *
   * Качването е multipart POST на `list`: файл, дружество, вид и адресат.
   */
  documents: {
    list: 'documents/',
    detail: detail('documents/'),
    download: (id: number | string) => `documents/${id}/download/`,
    /** Същият файл, показан в браузъра вместо свален — само за безопасни типове. */
    preview: (id: number | string) => `documents/${id}/download/?inline=1`,
    /** Колко умалени страници има документът · { count } */
    pages: (id: number | string) => `documents/${id}/pages/`,
    /** Една умалена страница като JPEG, от 1. */
    page: (id: number | string, page: number) => `documents/${id}/pages/${page}/`,
    /** Всички файлове и към какво е закачен всеки · ?source=scan|upload&group=&attached=yes|no */
    files: 'documents/files/',
  },

  reports: {
    /** ?company=<id> — всички текущи задължения по категории. */
    payables: 'reports/payables/',
    /** ?company=&method=&source=&counterparty=&project=&date_from=&date_to=&search= — изходящи плащания. */
    payments: 'reports/payments/',
    /** ?company=&months=&limit= — движение по месеци и текущите обекти. */
    dashboard: 'reports/dashboard/',
    /** ?date=YYYY-MM-DD&company=<id> — матрица обекти × контрагенти. */
    weeklyPaymentRun: 'reports/weekly-payment-run/',
    counterpartyBalances: 'reports/counterparty-balances/',
    /** ?from=YYYY-MM-DD&to=YYYY-MM-DD */
    projectCosts: 'reports/project-costs/',
    /** ?company=&project=&counterparty=&open=1 — договорено/изпълнено/остава. */
    contractDrawdown: 'reports/contract-drawdown/',
    /**
     * Експортите приемат същите филтри като екрана, от който тръгват, плюс
     * `format=xlsx|csv`. Файл, който пренебрегва филтрите, връща хората в Excel.
     */
    exportTransactions: 'reports/export/transactions/',
    exportWorkOrders: 'reports/export/work-orders/',
    exportWeeklyPaymentRun: 'reports/export/weekly-payment-run/',
    exportCounterpartyBalances: 'reports/export/counterparty-balances/',
    exportProjectCosts: 'reports/export/project-costs/',
  },

  /**
   * Псевдоними — суровите имена, които импортът не е успял да свърже.
   *
   * Четирите таблици се държат еднакво, затова и адресите им се раждат от една
   * функция: екранът за свързване работи с „вид“, а не с четири отделни пътя.
   */
  aliases: {
    counterparty: aliasEndpoints('counterparty-aliases/'),
    project: aliasEndpoints('project-aliases/'),
    costCentre: aliasEndpoints('cost-centre-aliases/'),
    category: aliasEndpoints('category-aliases/'),
  },
} as const

/**
 * Адресите на една таблица с псевдоними.
 *
 * Списъкът приема `resolved=false`, `suggested=true|false`, `search`, `limit`,
 * `offset` — свързването на единайсет години свободен текст не се побира на
 * една страница и никога не се тегли наведнъж.
 *
 * Обявена като функция (не като `const`), за да е издигната: обектът по-горе я
 * вика при зареждане на модула.
 */
function aliasEndpoints(base: string) {
  return {
    list: base,
    detail: detail(base),
    /** Потвърждава предложението на импорта такова, каквото е. */
    confirm: (id: number | string) => `${base}${id}/confirm/`,
    /** POST {target: <id>} — свързва името със съществуващ запис. */
    link: (id: number | string) => `${base}${id}/link/`,
    /** POST {name, ...} — създава нов запис и свързва името с него. */
    createTarget: (id: number | string) => `${base}${id}/create_target/`,
    /** POST {ids: [...]} — потвърждава наведнъж предложенията на страницата. */
    bulkConfirm: `${base}bulk_confirm/`,
  }
}
